import * as React from "react";
import {
  Select as ShadcnSelect,
  SelectTrigger as ShadcnSelectTrigger,
  SelectContent as ShadcnSelectContent,
  SelectItem as ShadcnSelectItem,
  SelectValue as ShadcnSelectValue,
  SelectGroup as ShadcnSelectGroup,
  SelectLabel as ShadcnSelectLabel,
  SelectSeparator as ShadcnSelectSeparator,
} from "../select";
import { cn } from "@/lib/utils";

// SelectItem with Hero UI compatible props
export interface SelectItemProps extends Omit<
  React.ComponentPropsWithoutRef<typeof ShadcnSelectItem>,
  "value"
> {
  key?: string;
  textValue?: string;
  value?: string;
}

const SelectItem = React.forwardRef<
  React.ElementRef<typeof ShadcnSelectItem>,
  SelectItemProps
>(({ className, children, key, textValue, value, ...props }, ref) => {
  // Use value prop if provided, otherwise use key or textValue
  const itemValue = value ?? key ?? textValue ?? String(children) ?? "";

  return (
    <ShadcnSelectItem
      ref={ref}
      value={itemValue}
      className={className}
      {...props}
    >
      {children}
    </ShadcnSelectItem>
  );
});
SelectItem.displayName = "SelectItem";

// Select with Hero UI compatible props
export interface SelectProps<T = any> extends Omit<
  React.ComponentPropsWithoutRef<typeof ShadcnSelect>,
  "value" | "onValueChange" | "children"
> {
  items?: T[];
  selectedKeys?: string[] | Set<string>;
  onSelectionChange?: (keys: string[] | Set<string>) => void;
  selectionMode?: "single" | "multiple";
  disallowEmptySelection?: boolean;
  renderValue?: (items: Array<{ key: string; data: T }>) => React.ReactNode;
  classNames?: {
    base?: string;
    label?: string;
    trigger?: string;
    listbox?: string;
    selectorIcon?: string;
    popoverContent?: string;
    innerWrapper?: string;
  };
  label?: string;
  placeholder?: string;
  labelPlacement?: "outside" | "inside" | "outside-left";
  variant?: "flat" | "bordered" | "faded" | "underlined";
  radius?: "none" | "sm" | "md" | "lg" | "full";
  className?: string;
  children?: ((item: T) => React.ReactNode) | React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
}

const Select = React.forwardRef<HTMLDivElement, SelectProps<any>>(
  (
    {
      items,
      selectedKeys: controlledSelectedKeys,
      onSelectionChange,
      selectionMode = "single",
      disallowEmptySelection = false,
      renderValue,
      classNames,
      label,
      placeholder,
      labelPlacement = "outside",
      variant = "bordered",
      radius = "lg",
      children,
      className,
      side,
      align,
      ...props
    },
    ref
  ) => {
    const [internalSelectedKeys, setInternalSelectedKeys] = React.useState<
      Set<string>
    >(new Set());
    const isControlled = controlledSelectedKeys !== undefined;

    const selectedKeysSet = React.useMemo(() => {
      if (isControlled) {
        if (Array.isArray(controlledSelectedKeys)) {
          return new Set(controlledSelectedKeys);
        }
        if (controlledSelectedKeys instanceof Set) {
          return controlledSelectedKeys;
        }
        return new Set();
      }
      return internalSelectedKeys;
    }, [controlledSelectedKeys, internalSelectedKeys, isControlled]);

    // Convert Set to string for Radix Select (single selection)
    const selectedValue = React.useMemo((): string | undefined => {
      const keysArray = Array.from(selectedKeysSet);
      if (keysArray.length === 0) return undefined;
      return String(keysArray[0]); // Radix Select only supports single selection
    }, [selectedKeysSet]);

    const handleValueChange = React.useCallback(
      (value: string | undefined) => {
        if (value === undefined) {
          if (disallowEmptySelection) return;
          const newKeys = new Set<string>();
          if (!isControlled) {
            setInternalSelectedKeys(newKeys);
          }
          if (onSelectionChange) {
            onSelectionChange(selectionMode === "single" ? [] : newKeys);
          }
          return;
        }

        const newKeys = new Set<string>();
        newKeys.add(value);

        if (!isControlled) {
          setInternalSelectedKeys(newKeys);
        }

        if (onSelectionChange) {
          onSelectionChange(selectionMode === "single" ? [value] : newKeys);
        }
      },
      [isControlled, onSelectionChange, selectionMode, disallowEmptySelection]
    );

    const variantClasses = {
      flat: "border-0",
      bordered: "border-1.5",
      faded: "border",
      underlined: "border-b-2 border-t-0 border-l-0 border-r-0 rounded-none",
    };

    const radiusClasses = {
      none: "rounded-none",
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      full: "rounded-full",
    };

    const renderItems = (): React.ReactNode => {
      if (!items || items.length === 0) {
        return null;
      }

      if (typeof children === "function") {
        return items.map((item: any, index: number): React.ReactNode => {
          const itemKey = String(
            item?.value ?? item?.chain_id ?? item?.id ?? item ?? index
          );
          const itemElement = children(item);

          if (React.isValidElement(itemElement)) {
            // Preserve existing value if already set, otherwise use itemKey
            const existingValue = (itemElement as React.ReactElement<any>).props
              ?.value;
            const finalValue = existingValue ? String(existingValue) : itemKey;
            return React.cloneElement(itemElement as React.ReactElement<any>, {
              key: itemKey,
              value: finalValue,
            });
          }
          return itemElement;
        });
      }

      return children;
    };

    // Get selected items for renderValue
    const selectedItems = React.useMemo(() => {
      if (!items || !renderValue) return [];
      return Array.from(selectedKeysSet)
        .map((key) => {
          const item = items.find(
            (item: any) =>
              String(item?.value ?? item?.chain_id ?? item?.id ?? item) ===
              String(key)
          );
          return item ? { key, data: item } : null;
        })
        .filter((item): item is { key: string; data: any } => item !== null);
    }, [items, selectedKeysSet, renderValue]);

    return (
      <div className={cn("flex flex-col gap-2", classNames?.base, className)}>
        {label && labelPlacement === "outside" && (
          <span className={classNames?.label}>{label}</span>
        )}
        <ShadcnSelect
          value={selectedValue}
          onValueChange={handleValueChange}
          disabled={disallowEmptySelection && selectedKeysSet.size === 0}
          {...props}
        >
          <SelectTrigger
            className={cn(
              variantClasses[variant],
              radiusClasses[radius],
              classNames?.trigger
            )}
          >
            {renderValue && selectedItems.length > 0 ? (
              <div
                className={cn("flex items-center", classNames?.innerWrapper)}
              >
                {renderValue(selectedItems)}
              </div>
            ) : (
              <SelectValue placeholder={placeholder} />
            )}
          </SelectTrigger>
          <SelectContent
            className={cn(classNames?.popoverContent, classNames?.listbox)}
            side={side}
            align={align}
          >
            {renderItems()}
          </SelectContent>
        </ShadcnSelect>
      </div>
    );
  }
);
Select.displayName = "Select";

// Re-export other components
export { Select, SelectItem };
export const SelectTrigger = ShadcnSelectTrigger;
export const SelectContent = ShadcnSelectContent;
export const SelectValue = ShadcnSelectValue;
export const SelectGroup = ShadcnSelectGroup;
export const SelectLabel = ShadcnSelectLabel;
export const SelectSeparator = ShadcnSelectSeparator;
